/* ============================================================================
   CX MODEL DRAWER — unified controller
   Modes:
     header     — bound to header SELECT MODEL summary (desktop + mobile)
     collection — inline trigger on *-cases collection toolbar
     pdp        — inline trigger on product page

   Behavior:
     header / collection -> navigate to /collections/{slug}-cases
       (preserves filter.* & sort_by when current page is a *-cases collection)
     pdp -> existence-check /products/{design}-{slug}-case.js, then navigate
============================================================================ */

(function () {
  'use strict';

  var SS_PREFIX = 'cx_pdp_exists__';

  var drawer, panel, body, search, notice, items;
  var currentMode = null;
  var currentModel = '';
  var currentDesign = '';
  var initialized = false;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function handleize(s) {
    return String(s || '').trim().toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function slugToLabel(slug) {
    var s = String(slug || '').trim();
    if (!s) return '';
    var words = s.split('-').filter(Boolean);
    var map = {
      iphone: 'iPhone', samsung: 'Samsung', galaxy: 'Galaxy',
      pro: 'Pro', max: 'Max', ultra: 'Ultra', plus: 'Plus',
      mini: 'Mini', fe: 'FE'
    };
    for (var i = 0; i < words.length; i++) {
      var w = words[i].toLowerCase();
      if (map[w]) words[i] = map[w];
      else if (/^\d+$/.test(w)) words[i] = w;
      else words[i] = w.charAt(0).toUpperCase() + w.slice(1);
    }
    return words.join(' ');
  }

  // ---------- Existence check (PDP) ----------
  function checkExists(handle) {
    return new Promise(function (resolve) {
      if (!handle) return resolve(false);
      try {
        var v = sessionStorage.getItem(SS_PREFIX + handle);
        if (v === '1') return resolve(true);
        if (v === '0') return resolve(false);
      } catch (e) {}

      fetch('/products/' + handle + '.js', {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      })
        .then(function (res) {
          var ok = !!(res && res.ok);
          try { sessionStorage.setItem(SS_PREFIX + handle, ok ? '1' : '0'); } catch (e) {}
          resolve(ok);
        })
        .catch(function () {
          try { sessionStorage.setItem(SS_PREFIX + handle, '0'); } catch (e) {}
          resolve(false);
        });
    });
  }

  // ---------- Body scroll lock ----------
  function lockBody() {
    document.documentElement.classList.add('cx-md-locked');
    document.body.classList.add('cx-md-locked');
  }
  function unlockBody() {
    document.documentElement.classList.remove('cx-md-locked');
    document.body.classList.remove('cx-md-locked');
  }

  // ---------- Collection URL builder (preserves filters/sort) ----------
  function buildCollectionUrl(slug) {
    var path = '/collections/' + slug + '-cases';
    var onCases = /^\/collections\/[^\/]+-cases\/?$/i.test(window.location.pathname);
    if (!onCases) return path;
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('page');
      return path + (url.search || '');
    } catch (e) {
      return path;
    }
  }

  // ---------- Drawer state ----------
  function setNotice(msg, isErr) {
    if (!notice) return;
    notice.textContent = msg || '';
    if (isErr) notice.classList.add('is-error');
    else notice.classList.remove('is-error');
  }

  function applyActiveState() {
    for (var i = 0; i < items.length; i++) {
      var slug = items[i].getAttribute('data-model-slug') || '';
      if (slug && slug === currentModel) items[i].classList.add('is-active');
      else items[i].classList.remove('is-active');
      items[i].classList.remove('is-loading');
    }
    if (body) body.classList.remove('is-busy');
  }

  function filterList(q) {
    var query = String(q || '').toLowerCase();
    for (var i = 0; i < items.length; i++) {
      var t = (items[i].textContent || '').toLowerCase();
      items[i].style.display = t.indexOf(query) !== -1 ? '' : 'none';
    }
  }

  function scrollToActive() {
    var active = drawer.querySelector('.cx-md-item.is-active');
    if (!active) return;
    try { active.scrollIntoView({ block: 'center', behavior: 'auto' }); } catch (e) {}
  }

  function open(mode, model, design) {
    currentMode = mode || 'header';
    currentModel = handleize(model || '');
    currentDesign = handleize(design || '');

    setNotice('', false);
    applyActiveState();

    drawer.setAttribute('data-cx-md-mode', currentMode);
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockBody();

    if (search) {
      search.value = '';
      filterList('');
      setTimeout(function () { try { search.focus(); } catch (e) {} }, 30);
    }

    setTimeout(scrollToActive, 60);
  }

  function close() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (body) body.classList.remove('is-busy');
    for (var i = 0; i < items.length; i++) items[i].classList.remove('is-loading');
    unlockBody();
  }

  // ---------- Item click ----------
  function onItemClick(btn) {
    var slug = btn.getAttribute('data-model-slug') || '';
    if (!slug) return;

    if (currentMode === 'pdp') {
      if (!currentDesign) {
        setNotice('Not available', true);
        return;
      }
      var handle = currentDesign + '-' + slug + '-case';
      if (handle.indexOf('--') !== -1) return;

      btn.classList.add('is-loading');
      body.classList.add('is-busy');
      setNotice('', false);

      checkExists(handle).then(function (ok) {
        btn.classList.remove('is-loading');
        body.classList.remove('is-busy');
        // Bail if drawer was closed mid-fetch
        if (!drawer.classList.contains('is-open')) return;
        if (ok) {
          window.location.href = '/products/' + handle;
        } else {
          setNotice('Not available', true);
        }
      });
      return;
    }

    // header or collection -> model collection
    window.location.href = buildCollectionUrl(slug);
  }

  // ---------- Trigger label updater ----------
  function updateTriggerLabels() {
    $$('[data-cx-md-trigger-label]').forEach(function (el) {
      var trigger = el.closest('[data-cx-md-open]');
      if (!trigger) return;
      var slug = trigger.getAttribute('data-cx-current-model') || '';
      el.textContent = slugToLabel(slug) || 'Select model';
    });
  }

  // ---------- Header trigger detection ----------
  function getHeaderTrigger(target) {
    if (!target || !target.closest) return null;
    if (target.id === 'HeaderDrawer-select-model') return target;
    if (target.id === 'HeaderMenu-select-model') return target;

    var hd = target.closest('#HeaderDrawer-select-model');
    if (hd) return hd;

    var hm = target.closest('#HeaderMenu-select-model');
    if (hm) return hm;

    var details = target.closest('details.mega-menu[data-handle="select-model"]');
    if (details) {
      var summary = target.closest('summary');
      if (summary && details.contains(summary)) return summary;
    }
    return null;
  }

  // ---------- Init ----------
  function init() {
    if (initialized) return;
    drawer = $('[data-cx-md]');
    if (!drawer) return;
    initialized = true;

    panel = $('.cx-md__panel', drawer);
    body = $('[data-cx-md-list]', drawer);
    search = $('[data-cx-md-search]', drawer);
    notice = $('[data-cx-md-notice]', drawer);
    items = $$('[data-cx-md-item]', drawer);

    // Portal drawer to <body>
    if (drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }

    // Close
    $$('[data-cx-md-close]', drawer).forEach(function (el) {
      el.addEventListener('click', close);
    });
    drawer.addEventListener('click', function (e) {
      if (panel && !panel.contains(e.target)) close();
    });
    document.addEventListener('keydown', function (e) {
      var k = e.key || e.keyCode;
      if ((k === 'Escape' || k === 'Esc' || k === 27) && drawer.classList.contains('is-open')) {
        close();
      }
    });

    // Search
    if (search) {
      search.addEventListener('input', function () { filterList(search.value); });
    }

    // Item clicks
    items.forEach(function (btn) {
      btn.addEventListener('click', function () { onItemClick(btn); });
    });

    // Delegated trigger handler — covers inline triggers + header
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t) return;

      var inline = t.closest && t.closest('[data-cx-md-open]');
      if (inline) {
        e.preventDefault();
        e.stopPropagation();
        var mode = inline.getAttribute('data-cx-md-mode') || 'collection';
        var model = inline.getAttribute('data-cx-current-model') || '';
        var design = inline.getAttribute('data-cx-current-design') || '';
        var scrollTo = inline.getAttribute('data-cx-md-scroll-to') || '';
        open(mode, model, design);
        if (scrollTo) {
          setTimeout(function () {
            var group = drawer.querySelector('[data-cx-md-group="' + scrollTo + '"]');
            if (!group || !body) return;
            // Use bounding rects so the calc is correct regardless of offsetParent.
            var bodyRect = body.getBoundingClientRect();
            var groupRect = group.getBoundingClientRect();
            var target = (groupRect.top - bodyRect.top) + body.scrollTop - 4;
            if (target < 0) target = 0;
            try { body.scrollTo({ top: target, behavior: 'smooth' }); }
            catch (err) { body.scrollTop = target; }
          }, 140);
        }
        return;
      }

      var header = getHeaderTrigger(t);
      if (header) {
        e.preventDefault();
        e.stopPropagation();
        var det = header.closest('details');
        if (det) { try { det.removeAttribute('open'); } catch (err) {} }

        // Derive active model from URL if on a *-cases collection,
        // otherwise from any inline trigger present on the page (e.g. PDP).
        var model = '';
        var pathMatch = (window.location.pathname || '').match(/\/collections\/([^\/]+)-cases\/?$/i);
        if (pathMatch) {
          model = pathMatch[1];
        } else {
          var hint = document.querySelector('[data-cx-md-open][data-cx-current-model]');
          if (hint) model = hint.getAttribute('data-cx-current-model') || '';
        }
        open('header', model, '');
      }
    }, true);
  }

  function boot() {
    init();
    updateTriggerLabels();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:reorder', boot);
})();
